// Hand-rolled, dependency-free bar charts. Pure props -> markup; a one-shot
// useIso reveal grows the bars in on mount (browser-only), so they still
// render at full size under renderToStaticMarkup (SSR).

import { useRef } from 'react'
import { gsap, prefersReduced } from '../../lib/gsap.js'
import useIso from '../../ds/hooks/useIso.js'

/**
 * BarChart — a column of labeled horizontal bars.
 *
 * `data = [{ id, name, color, count, value, raw }]`. The numeric field charted
 * is picked per-datum in this order: `value`, then `raw`, then `count` — so
 * callers can shape data with whichever field reads best (e.g. metrics.byStage
 * has both `count` and `raw`; pass `raw` for money bars by mapping to `value`,
 * or just rely on the fallback order). `max` is the largest of those numbers
 * across `data`; guarded against 0 (renders zero-width bars, no divide-by-zero).
 * `valueFmt(n)` formats the number shown at the right of each bar (default:
 * identity — renders the raw number).
 */
export function BarChart({ data = [], valueFmt = (n) => n, hrefFor = null }) {
  const rootRef = useRef(null)
  const played = useRef(false)
  const numOf = (d) => (typeof d.value === 'number' ? d.value : typeof d.raw === 'number' ? d.raw : typeof d.count === 'number' ? d.count : 0)
  const max = data.reduce((m, d) => Math.max(m, numOf(d)), 0)
  useIso(() => {
    if (played.current) return
    const el = rootRef.current
    if (!el) return
    if (prefersReduced()) { played.current = true; return }
    const bars = el.querySelectorAll('[data-bar-fill]')
    if (!bars.length) return
    played.current = true
    gsap.from(bars, { scaleX: 0, transformOrigin: 'left center', duration: 0.5, stagger: 0.05, ease: 'power2.out' })
  }, [data.length])
  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {data.map((d) => {
        const n = numOf(d)
        const pct = max === 0 ? 0 : (n / max) * 100
        const href = hrefFor ? hrefFor(d) : null
        const row = (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: '0 0 96px', fontSize: 12, color: 'var(--color-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
            <div style={{ flex: 1, height: 8, borderRadius: 'var(--radius-sm)', background: 'var(--color-divider-dark)', overflow: 'hidden' }}>
              <div data-bar-fill style={{ width: `${pct}%`, height: '100%', background: d.color || 'var(--color-gold)', borderRadius: 'var(--radius-sm)' }} />
            </div>
            <span className="font-mono" style={{ flex: '0 0 auto', minWidth: 40, textAlign: 'right', fontSize: 11, color: 'var(--color-white)' }}>{valueFmt(n)}</span>
          </div>
        )
        return href
          ? <a key={d.id} href={href} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>{row}</a>
          : <div key={d.id}>{row}</div>
      })}
      {data.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>No data yet.</div>}
    </div>
  )
}

/**
 * MonthlyBars — a row of vertical bars, one per month.
 *
 * `data = [{ label, count }]` (the shape returned by `monthlyCounts`). Bar
 * height is proportional to `count / max` of a fixed track height; guarded
 * against `max === 0` (renders zero-height bars, no divide-by-zero).
 */
export function MonthlyBars({ data = [], color = 'var(--color-gold)' }) {
  const rootRef = useRef(null)
  const played = useRef(false)
  const TRACK = 64
  const max = data.reduce((m, d) => Math.max(m, d.count || 0), 0)
  useIso(() => {
    if (played.current) return
    const el = rootRef.current
    if (!el) return
    if (prefersReduced()) { played.current = true; return }
    const bars = el.querySelectorAll('[data-bar-col]')
    if (!bars.length) return
    played.current = true
    gsap.from(bars, { scaleY: 0, transformOrigin: 'bottom center', duration: 0.5, stagger: 0.04, ease: 'power2.out' })
  }, [data.length])
  return (
    <div ref={rootRef} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: TRACK + 20 }}>
      {data.map((d, i) => {
        const h = max === 0 ? 0 : (d.count / max) * TRACK
        return (
          <div key={d.label + i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: TRACK + 20 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', width: '100%' }}>
              <div data-bar-col style={{ width: '100%', height: h, minHeight: d.count > 0 ? 2 : 0, background: color, borderRadius: '2px 2px 0 0' }} />
            </div>
            <span className="font-mono" style={{ fontSize: 10, color: 'var(--color-muted)', marginTop: 6 }}>{d.label}</span>
          </div>
        )
      })}
    </div>
  )
}
